export const MY_BRIEF_STATUSES = ['in_progress', 'ready', 'failed', 'regenerating', 'deleted'];

export const MY_BRIEF_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'status', label: 'Status' },
  { value: 'language', label: 'Language' },
];

export const MY_BRIEF_DATE_RANGES = [
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'all_time', label: 'All time' },
];

export const MY_BRIEF_DEFAULT_FILTERS = {
  ownerId: 'mine',
  status: '',
  language: '',
  dateRange: 'last_7_days',
  sort: 'newest',
  // '' = everything. Newsletters live in this same list, tagged by contentType.
  contentType: '',
};

// A newsletter and a media analysis are both ASSEMBLED artifacts: composed
// from many bucket stories rather than written from one. None of the
// story-shaped affordances apply to either — there is no single story to
// regenerate from, no source article count, and neither is a news item a CMS
// would accept. A list rather than two checks, because the next assembled type
// on this rail needs the same treatment and forgetting is silent: the buttons
// simply render and then misbehave when pressed.
export const ASSEMBLED_CONTENT_TYPES = ['newsletter', 'media_analysis'];

export function isAssembledContentType(contentType) {
  return ASSEMBLED_CONTENT_TYPES.includes(contentType);
}

/** Chip shown beside the title, or null for an ordinary brief. */
export function contentTypeChipLabel(contentType) {
  switch (contentType) {
    case 'newsletter': return 'Newsletter';
    case 'media_analysis': return 'Media analysis';
    default: return null;
  }
}

export function untitledLabelFor(contentType) {
  switch (contentType) {
    case 'newsletter': return 'Untitled newsletter';
    case 'media_analysis': return 'Untitled media analysis';
    default: return 'Untitled story';
  }
}

export const MY_BRIEF_CONTENT_TYPES = [
  { value: '', label: 'All content' },
  { value: 'brief', label: 'Drafts' },
  { value: 'newsletter', label: 'Newsletters' },
  { value: 'media_analysis', label: 'Media analyses' },
];

export function formatStatusLabel(status) {
  switch (status) {
    case 'in_progress': return 'In progress';
    case 'ready': return 'Ready';
    case 'failed': return 'Failed';
    case 'regenerating': return 'Regenerating';
    case 'deleted': return 'Archived';
    default: return status || 'Unknown';
  }
}

export function isActiveStatus(status) {
  return status === 'in_progress' || status === 'regenerating';
}

export function formatRelativeTime(value, nowMs = Date.now()) {
  if (!value) return 'Unknown time';
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return 'Unknown time';

  const diffMs = Math.max(0, nowMs - timestamp);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < minuteMs) return 'Just now';

  if (diffMs < hourMs) {
    const minutes = Math.floor(diffMs / minuteMs);
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  if (diffMs < dayMs) {
    const hours = Math.floor(diffMs / hourMs);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(diffMs / dayMs);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function getLocalDayKey(value) {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDraftDayDividerLabel(value, nowMs = Date.now()) {
  const target = new Date(value);
  const now = new Date(nowMs);
  if (Number.isNaN(target.getTime())) return 'Unknown date';

  const todayKey = getLocalDayKey(now);
  const targetKey = getLocalDayKey(target);
  if (targetKey === todayKey) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (targetKey === getLocalDayKey(yesterday)) return 'Yesterday';

  const day = String(target.getDate()).padStart(2, '0');
  const month = String(target.getMonth() + 1).padStart(2, '0');
  const year = target.getFullYear();
  return `${day}/${month}/${year}`;
}

export function getDraftDayGroupKey(value) {
  return getLocalDayKey(value);
}

const SUPPORTED_RICH_TEXT_TAG_PATTERN = /<(p|div|br|strong|b|em|i|u|s|ul|ol|li|blockquote|a|span)\b/i;

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function hasRichTextFormatting(value) {
  if (typeof value !== 'string') return false;
  return SUPPORTED_RICH_TEXT_TAG_PATTERN.test(value);
}

export function normalizeBriefBodyForEditor(value) {
  if (!value) return '';
  if (hasRichTextFormatting(value)) return String(value).trim();

  const paragraphs = String(value)
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (!paragraphs.length) return '';

  return paragraphs
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function normalizeBriefBodyEditorOutput(value) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '<p><br></p>' || trimmed === '<div><br></div>') return '';
  return trimmed;
}

// Recognized "Sources:" label spellings across every language the app
// supports. Used to strip the trailing footer from the brief body before
// copying so the clipboard only contains article content.
const SOURCES_LABEL_ALTERNATION = [
  'Sources', 'Quellen', 'Fuentes', 'Fontes', 'Fonti',
  'מקורות', 'المصادر', 'Источники', 'Πηγές',
  'Bronnen', 'Kilder', 'Zdroje', 'Kaynaklar',
  'Джерела', 'منابع', '来源', 'स्रोत',
].join('|');

const SOURCES_FOOTER_HTML_REGEX = new RegExp(
  `(?:<p[^>]*>\\s*)?(?:${SOURCES_LABEL_ALTERNATION})\\s*[:：][\\s\\S]*$`,
  'i'
);
const SOURCES_FOOTER_TEXT_REGEX = new RegExp(
  `\\n+\\s*(?:${SOURCES_LABEL_ALTERNATION})\\s*[:：][\\s\\S]*$`,
  'i'
);

export function stripSourcesFooter(value) {
  if (!value) return '';
  return String(value).replace(SOURCES_FOOTER_HTML_REGEX, '');
}

export function getPlainTextFromBriefBody(value, { excludeSources = true } = {}) {
  if (!value) return '';
  let working = excludeSources ? stripSourcesFooter(value) : String(value);
  if (!hasRichTextFormatting(working)) {
    const text = working.trim();
    return excludeSources ? text.replace(SOURCES_FOOTER_TEXT_REGEX, '').trim() : text;
  }

  const normalizedHtml = working
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|blockquote|h[1-6])>/gi, '\n');

  let plain;
  if (typeof document !== 'undefined') {
    const container = document.createElement('div');
    container.innerHTML = normalizedHtml;
    plain = (container.textContent || '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  } else {
    plain = normalizedHtml
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  return excludeSources ? plain.replace(SOURCES_FOOTER_TEXT_REGEX, '').trim() : plain;
}

// ---------------------------------------------------------------------------
// Clipboard conversion
//
// Draft bodies are rich text (Quill: bold/italic/underline/strike, lists,
// blockquote, links). Copying them needs two flavors: sanitized `text/html`
// for rich destinations, and a `text/plain` rendering that keeps every link
// destination visible.
// ---------------------------------------------------------------------------

const CLIPBOARD_ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'DEL', 'INS',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI', 'BLOCKQUOTE', 'SPAN', 'DIV',
]);

// Dropped together with their content — never merely unwrapped.
const CLIPBOARD_DROPPED_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'IMG', 'PICTURE', 'SOURCE',
  'SVG', 'MATH', 'VIDEO', 'AUDIO', 'TRACK', 'LINK', 'META', 'BASE', 'TITLE',
  'FORM', 'INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'OPTION', 'LABEL',
  'NOSCRIPT', 'TEMPLATE', 'CANVAS', 'APPLET', 'FRAME', 'FRAMESET',
]);

const SAFE_HREF_PATTERN = /^(?:https?:|mailto:)/i;

// Returns the href to keep, or '' when the anchor must be unwrapped.
function getSafeHref(rawHref) {
  if (!rawHref) return '';
  // Strip control characters and whitespace used to smuggle "java\nscript:".
  const candidate = String(rawHref).replace(/[\s\u0000-\u001f\u007f-\u009f]/g, '');
  if (!candidate) return '';
  return SAFE_HREF_PATTERN.test(candidate) ? candidate : '';
}

// Parses untrusted HTML inertly (DOMParser does not run scripts or fetch
// subresources). Returns null when no DOM is available.
function parseHtmlInert(html) {
  if (typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    return doc?.body || null;
  } catch (_error) {
    return null;
  }
}

function copySanitizedChildren(source, target, doc) {
  Array.from(source.childNodes || []).forEach((child) => {
    if (child.nodeType === 3) {
      target.appendChild(doc.createTextNode(child.nodeValue || ''));
      return;
    }
    if (child.nodeType !== 1) return;

    const tag = String(child.tagName || '').toUpperCase();
    if (CLIPBOARD_DROPPED_TAGS.has(tag)) return;

    if (tag === 'A') {
      const href = getSafeHref(child.getAttribute('href'));
      if (!href) {
        // Unsafe or missing destination: drop the anchor, keep its text.
        copySanitizedChildren(child, target, doc);
        return;
      }
      const anchor = doc.createElement('a');
      anchor.setAttribute('href', href);
      copySanitizedChildren(child, anchor, doc);
      target.appendChild(anchor);
      return;
    }

    if (!CLIPBOARD_ALLOWED_TAGS.has(tag)) {
      copySanitizedChildren(child, target, doc);
      return;
    }

    // Rebuilding the element drops every attribute (style, class, on*, …).
    const element = doc.createElement(tag.toLowerCase());
    copySanitizedChildren(child, element, doc);
    target.appendChild(element);
  });
}

/**
 * Strict-allowlist sanitizer for clipboard HTML. Keeps the formatting the
 * draft editor can produce, keeps only validated http/https/mailto anchors,
 * and drops every attribute other than those hrefs.
 */
export function sanitizeBriefHtmlForClipboard(html) {
  if (!html) return '';
  const parsed = parseHtmlInert(String(html));
  if (!parsed) {
    // No DOM (SSR/node): never emit markup we could not inspect.
    return escapeHtml(getPlainTextFromBriefBody(String(html), { excludeSources: false }));
  }
  const doc = parsed.ownerDocument;
  const container = doc.createElement('div');
  copySanitizedChildren(parsed, container, doc);
  return container.innerHTML;
}

function formatLinkForPlainText(text, href) {
  const label = (text || '').trim();
  if (!label) return href;
  if (label === href) return label;
  return `${label} (${href})`;
}

// Replaces every anchor with a plain-text rendering of itself so link
// destinations survive text extraction: `Example (https://example.com)`,
// or just the URL when the anchor text already is the URL.
function annotateLinksForPlainText(html) {
  if (!html) return '';
  const parsed = parseHtmlInert(String(html));
  if (!parsed) return String(html);

  const doc = parsed.ownerDocument;
  Array.from(parsed.querySelectorAll('a')).forEach((anchor) => {
    const href = getSafeHref(anchor.getAttribute('href'));
    const label = anchor.textContent || '';
    const replacement = href ? formatLinkForPlainText(label, href) : label.trim();
    anchor.parentNode?.replaceChild(doc.createTextNode(replacement), anchor);
  });
  return parsed.innerHTML;
}

/**
 * Link-aware variant of {@link getPlainTextFromBriefBody}. Kept separate so
 * existing search/preview callers keep their current output.
 */
export function getBriefBodyPlainTextWithLinks(value, { excludeSources = true } = {}) {
  if (!value) return '';
  const working = excludeSources ? stripSourcesFooter(value) : String(value);
  if (!hasRichTextFormatting(working)) {
    return getPlainTextFromBriefBody(working, { excludeSources });
  }
  return getPlainTextFromBriefBody(annotateLinksForPlainText(working), { excludeSources });
}

/**
 * Builds the sanitized `text/html` clipboard flavor for a draft. Header and
 * subheader are inserted as text nodes, never as markup.
 */
export function buildBriefCopyHtml({ header = '', subheader = '', body = '' } = {}) {
  const bodyHtml = sanitizeBriefHtmlForClipboard(normalizeBriefBodyForEditor(stripSourcesFooter(body)));
  const headerText = String(header || '').trim();
  const subheaderText = String(subheader || '').trim();
  if (!headerText && !subheaderText && !bodyHtml) return '';
  // Without a DOM there is no safe way to build the flavor; callers then copy
  // plain text only.
  if (typeof document === 'undefined') return '';

  const wrapper = document.createElement('div');
  wrapper.setAttribute('style', 'font-family: Arial, sans-serif');
  if (headerText) {
    const headerEl = document.createElement('h3');
    headerEl.textContent = headerText;
    wrapper.appendChild(headerEl);
  }
  if (subheaderText) {
    const subheaderEl = document.createElement('p');
    subheaderEl.textContent = subheaderText;
    wrapper.appendChild(subheaderEl);
  }
  if (bodyHtml) {
    const bodyEl = document.createElement('div');
    // Already sanitized above.
    bodyEl.innerHTML = bodyHtml;
    wrapper.appendChild(bodyEl);
  }
  return wrapper.outerHTML;
}

function isObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getSavedBriefComparisonVariants(detail) {
  const result = detail?.result;
  if (!isObject(result)) return [];

  const variants = [];
  if (isObject(result.claimsBased)) variants.push('claimsBased');
  if (isObject(result.claimFree)) variants.push('claimFree');
  return variants;
}

export function resolveSavedBriefResult(detail, preferredVariant = null) {
  const result = detail?.result;
  // A draft whose result was stored as a bare HTML string resolves to an empty
  // editor otherwise: every field reads blank while the body sits in the record.
  // Media Analysis wrote that shape until v13.0.2, so rows created before the
  // fix would stay unreadable without this. Treat the string as the body it is.
  if (typeof result === 'string' && result.trim()) {
    return {
      variant: null,
      availableVariants: [],
      hasComparison: false,
      content: { brief: result },
    };
  }
  if (!isObject(result)) {
    return {
      variant: null,
      availableVariants: [],
      hasComparison: false,
      content: {},
    };
  }

  const availableVariants = getSavedBriefComparisonVariants(detail);
  if (availableVariants.length === 0) {
    return {
      variant: null,
      availableVariants: [],
      hasComparison: false,
      content: result,
    };
  }

  let variant = availableVariants.includes(preferredVariant) ? preferredVariant : null;
  if (!variant) {
    const generationMethod = detail?.requestPayload?.generationMethod || detail?.generationMethod || result?.generationMethod || '';
    if (generationMethod === 'claimFree' && availableVariants.includes('claimFree')) {
      variant = 'claimFree';
    } else if (generationMethod === 'claims' && availableVariants.includes('claimsBased')) {
      variant = 'claimsBased';
    } else if (availableVariants.includes('claimsBased')) {
      variant = 'claimsBased';
    } else {
      variant = availableVariants[0];
    }
  }

  return {
    variant,
    availableVariants,
    hasComparison: availableVariants.length > 1,
    content: isObject(result[variant]) ? result[variant] : {},
  };
}
